import { TRPCClientError } from '@trpc/client'

import { trpc } from '@/clients/trpc'
import { reportSyncFailure } from './report-sync-failure'
import type { PullResponse } from './types'

type Model = 'interventi' | 'foto_interventi' | 'utenti' | 'committenti'

/** The server's cursor: the transaction that last wrote a row, and its key (K35). */
export type PullCursor = { xid: string; id: string }

/**
 * Where the next pull starts. `cursor` is sync.pull's. `lastPull` is the date
 * pullChanges pages on: what app versions before K35 stored, used only when the
 * API has no sync.pull yet (the UI deployed first, or the API rolled back).
 */
export type PullCheckpoint = { cursor?: PullCursor; lastPull?: Date }

/** The first checkpoint on a device that synced with a version before K35. */
export function initialPullCheckpoint(legacyLastPull: unknown): PullCheckpoint {
  return legacyLastPull instanceof Date ? { lastPull: legacyLastPull } : {}
}

/** A stored checkpoint, a bare date on devices from before K35. */
const asCheckpoint = (stored: PullCheckpoint | Date): PullCheckpoint =>
  stored instanceof Date ? { lastPull: stored } : stored

const compareStrings = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

// Transaction ids are unsigned integers sent as strings: compared by length,
// then digit by digit (BigInt would rule out older phones)
const compareXids = (a: string, b: string) =>
  a.length - b.length || compareStrings(a, b)

/**
 * Orders two checkpoints, negative when `a` is behind `b`, so the stored one
 * never moves backwards (K43): by cursor (transaction, then key) when both have
 * one, a cursor being ahead of none; otherwise, or with equal cursors, by
 * `lastPull`, which only the pullChanges fallback moves.
 */
export function comparePullCheckpoints(
  a: PullCheckpoint | Date,
  b: PullCheckpoint | Date
): number {
  const x = asCheckpoint(a)
  const y = asCheckpoint(b)
  if (x.cursor && y.cursor) {
    const byCursor =
      compareXids(x.cursor.xid, y.cursor.xid) ||
      compareStrings(x.cursor.id, y.cursor.id)
    if (byCursor) return byCursor
  } else if (x.cursor || y.cursor) {
    return x.cursor ? 1 : -1
  }
  // Neither has a lastPull: equal (the subtraction of two -Infinity is NaN)
  return (
    (x.lastPull?.getTime() ?? Number.NEGATIVE_INFINITY) -
      (y.lastPull?.getTime() ?? Number.NEGATIVE_INFINITY) || 0
  )
}

/** The API answered that a procedure (sync.pull, sync.push) doesn't exist. */
export function isMissingProcedure(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND'
}

/**
 * A pull handler for `modelName`. Without a cursor, sync.pull starts from the
 * beginning: a device's first pull after updating fetches every row it may see
 * once, and last-write-wins keeps its newer local copies.
 */
export function pullFromServer<R extends { updated_at: Date }>(
  modelName: Model
) {
  const pullPage = pullPageFromServer<R>(modelName)
  // Reported like push failures, which nothing reported before (K42, K44)
  return (stored: PullCheckpoint | Date | undefined, batchSize: number) =>
    pullPage(stored, batchSize).catch((error: unknown) => {
      reportSyncFailure(`trpc-${modelName}-pull`, error)
      throw error
    })
}

function pullPageFromServer<R extends { updated_at: Date }>(modelName: Model) {
  return async (
    stored: PullCheckpoint | Date | undefined,
    batchSize: number
  ): Promise<PullResponse<R, PullCheckpoint>> => {
    const checkpoint = stored && asCheckpoint(stored)
    try {
      const page = await trpc.sync.pull.query({
        modelName,
        cursor: checkpoint?.cursor,
        batchSize,
      })
      return {
        records: page.records as unknown as R[],
        checkpoint: { ...checkpoint, cursor: page.checkpoint },
        pending: page.pending,
      }
    } catch (error) {
      if (!isMissingProcedure(error)) throw error
    }

    // The API predates sync.pull: pull the way app versions before K35 did
    const records = (await trpc.sync.pullChanges.query({
      modelName,
      lastFinishedSyncEnd: checkpoint?.lastPull,
      batchSize,
    })) as unknown as R[]
    const lastRow = records.at(-1)
    return {
      records,
      checkpoint: {
        ...checkpoint,
        lastPull: lastRow ? new Date(lastRow.updated_at) : checkpoint?.lastPull,
      },
    }
  }
}
